// src/pages/UserForm.js
// Améliorations :
//   • Rôles en français (valeurs exactes de l'app Flutter)
//   • Multi-sélection de cours (selectedCourseIds[])
//   • Rôle Parent → crée/met à jour parents/
//   • registrationSource: 'admin'
//   • Champ pseudo (correctement nommé, pas username)
//   • Auto-liaison parent-enfant après création d'un élève
//   • UX : sections organisées, couleur MKD Studio

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
  doc, getDoc, setDoc, updateDoc,
  Timestamp, collection, getDocs
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon      from '@mui/icons-material/Edit';
import SaveIcon      from '@mui/icons-material/Save';
import {
  Box, Grid, TextField, Paper, Typography,
  FormControl, InputLabel, Select, MenuItem,
  Button, CircularProgress, Chip, Divider,
  Alert, Checkbox, ListItemText, OutlinedInput,
  FormHelperText
} from '@mui/material';

const PURPLE = '#7C3AED';

const ROLES = [
  { value: 'Élève',        label: 'Élève (mineur)',  color: 'secondary' },
  { value: 'Élève adulte', label: 'Élève adulte',    color: 'info'      },
  { value: 'Parent',       label: 'Parent',          color: 'warning'   },
  { value: 'Professeur',   label: 'Professeur',      color: 'primary'   },
  { value: 'Admin',        label: 'Administrateur',  color: 'error'     },
];

const PLANS = [
  { id: 'essai',          name: "Cours d'essai" },
  { id: 'unique',         name: 'Cours unique' },
  { id: 'carte10',        name: 'Carte 10 cours' },
  { id: 'bronze',         name: 'Abonnement Bronze' },
  { id: 'argent',         name: 'Abonnement Argent' },
  { id: 'annuel',         name: 'Abonnement Annuel' },
  { id: 'formation_crew', name: 'Formation Crew' },
];

const EMPTY = {
  firstName: '', lastName: '', pseudo: '', email: '', role: 'Élève',
  password: '', selectedCourseIds: [], registrationPlan: '',
  dateOfBirth: '', phone: '',
};

export default function UserForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);
  const [searchParams] = useSearchParams();

  // Préremplissage depuis un lien "Créer un compte élève" (ex: depuis
  // ParentForm.js quand un enfant lié n'a pas encore de compte) : évite à
  // l'admin de retaper le prénom/nom déjà connus.
  const prefill = {
    firstName: searchParams.get('firstName') || '',
    lastName:  searchParams.get('lastName')  || '',
    role:      searchParams.get('role')      || 'Élève',
  };

  const [userData,       setUserData]       = useState({ ...EMPTY, ...(!isEdit ? prefill : {}) });
  const [courses,        setCourses]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [loadingData,    setLoadingData]    = useState(isEdit);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');

  useEffect(() => {
    getDocs(collection(db, 'dance_courses')).then(snap => {
      setCourses(snap.docs.map(d => ({
        id: d.id,
        title: d.data().title_i18n?.fr || d.data().title || d.id,
      })).sort((a, b) => a.title.localeCompare(b.title)));
      setLoadingCourses(false);
    }).catch(() => setLoadingCourses(false));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    getDoc(doc(db, 'users', id)).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setUserData({
          firstName:         d.firstName         || '',
          lastName:          d.lastName          || '',
          pseudo:            d.pseudo            || '',
          email:             d.email             || '',
          role:              d.role              || 'Élève',
          password:          '',
          selectedCourseIds: d.selectedCourseIds || (d.courseId ? [d.courseId] : []),
          registrationPlan:  d.registrationPlan  || '',
          dateOfBirth:       d.dateOfBirth       || '',
          phone:             d.phone             || '',
        });
      }
    }).catch(() => setError("Impossible de charger l'utilisateur."))
      .finally(() => setLoadingData(false));
  }, [id, isEdit]);

  const handleChange = e => setUserData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleCourses = e => setUserData(p => ({ ...p, selectedCourseIds: e.target.value }));

  // Auto-liaison parent-enfant
  const autoLinkToParent = async (childUid, firstName, lastName) => {
    try {
      const snap = await getDocs(collection(db, 'parents'));
      for (const pDoc of snap.docs) {
        const pData = pDoc.data();
        const reqs  = pData.linkRequests ?? [];
        const rIdx  = reqs.findIndex(r =>
          r.status === 'pending' &&
          r.firstName?.toLowerCase() === firstName.toLowerCase() &&
          r.lastName?.toLowerCase()  === lastName.toLowerCase()
        );
        if (rIdx >= 0) {
          const newChildren = [...(pData.children ?? []), { uid: childUid, firstName, lastName }];
          const newReqs     = reqs.map((r, i) => i === rIdx ? { ...r, status: 'approved' } : r);
          await updateDoc(doc(db, 'parents', pDoc.id), { children: newChildren, linkRequests: newReqs });
          return;
        }
        const children = pData.children ?? [];
        const cIdx = children.findIndex(c =>
          !c.uid &&
          c.firstName?.toLowerCase() === firstName.toLowerCase() &&
          c.lastName?.toLowerCase()  === lastName.toLowerCase()
        );
        if (cIdx >= 0) {
          const newChildren = children.map((c, i) => i === cIdx ? { ...c, uid: childUid } : c);
          await updateDoc(doc(db, 'parents', pDoc.id), { children: newChildren });
          return;
        }
      }
    } catch (err) { console.warn('autoLinkToParent:', err.message); }
  };

  // Sync vers collection de rôle
  const syncRoleCollection = async (userId, role, profile, isNew) => {
    const base = {
      firstName: profile.firstName, lastName: profile.lastName,
      pseudo: profile.pseudo, email: profile.email, phone: profile.phone,
      dateOfBirth: profile.dateOfBirth,
      registrationSource: 'admin',
      [isNew ? 'createdAt' : 'updatedAt']: Timestamp.now(),
    };
    if (role === 'Professeur') {
      await setDoc(doc(db, 'teachers', userId),
        { ...base, courses: profile.selectedCourseIds || [] }, { merge: true });
    } else if (['Élève', 'Élève adulte'].includes(role)) {
      await setDoc(doc(db, 'students', userId), {
        ...base, userId, role,
        selectedCourseIds: profile.selectedCourseIds || [],
        courseId: profile.courseId || '',
        registrationPlan: profile.registrationPlan || '',
      }, { merge: true });
    } else if (role === 'Parent') {
      const allParents = await getDocs(collection(db, 'parents'));
      const existing   = allParents.docs.find(d => d.data().parentId === userId);
      const parentDoc  = {
        parentId: userId, parentFirstName: profile.firstName,
        parentLastName: profile.lastName, email: profile.email,
        phone: profile.phone, pseudo: profile.pseudo,
        children:     existing?.data().children     || [],
        linkRequests: existing?.data().linkRequests || [],
        registrationSource: 'admin',
        [isNew ? 'createdAt' : 'updatedAt']: Timestamp.now(),
      };
      if (existing) await updateDoc(existing.ref, parentDoc);
      else await setDoc(doc(db, 'parents', userId), parentDoc);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const { firstName, lastName, pseudo, email, role, password,
              selectedCourseIds, registrationPlan, dateOfBirth, phone } = userData;

      const userProfile = {
        firstName, lastName, pseudo, email, role, phone,
        selectedCourseIds,
        courseId: selectedCourseIds[0] || '',
        registrationPlan,
        registrationSource: 'admin',
        dateOfBirth: dateOfBirth ? Timestamp.fromDate(new Date(dateOfBirth)) : null,
      };

      if (isEdit) {
        await setDoc(doc(db, 'users', id),
          { ...userProfile, updatedAt: Timestamp.now() }, { merge: true });
        await syncRoleCollection(id, role, userProfile, false);
        setSuccess('Utilisateur mis à jour ✅');
        setTimeout(() => navigate('/users'), 1200);
        return;
      }

      // Création
      let userId;
      if (email && password) {
        const currentUser  = auth.currentUser;
        const currentToken = currentUser ? await currentUser.getIdToken() : null;
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        userId = cred.user.uid;
        // Note: rétablissement session admin doit être géré côté Firebase Admin SDK
      } else {
        userId = `manual_${Date.now()}`;
      }

      await setDoc(doc(db, 'users', userId),
        { ...userProfile, createdAt: Timestamp.now() });
      await syncRoleCollection(userId, role, userProfile, true);

      if (['Élève', 'Élève adulte'].includes(role)) {
        await autoLinkToParent(userId, firstName, lastName);
      }

      setSuccess('Utilisateur créé ✅');
      setTimeout(() => navigate('/users'), 1200);
    } catch (err) {
      console.error('UserForm:', err);
      setError(err.message || 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  if (loadingData) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
      <CircularProgress sx={{ color: PURPLE }} />
    </Box>
  );

  const isStudent = ['Élève', 'Élève adulte'].includes(userData.role);
  const isTeacher = userData.role === 'Professeur';
  const roleColor = ROLES.find(r => r.value === userData.role)?.color || 'default';

  return (
    <Box sx={{ p: 3, maxWidth: 860, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          {isEdit ? <EditIcon sx={{ color: PURPLE, fontSize: 28 }} />
                  : <PersonAddIcon sx={{ color: PURPLE, fontSize: 28 }} />}
          <Typography variant="h5" fontWeight={700}>
            {isEdit ? 'Modifier un utilisateur' : 'Créer un utilisateur'}
          </Typography>
          {userData.role && (
            <Chip label={userData.role} color={roleColor} size="small" sx={{ ml: 'auto' }} />
          )}
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>

            {/* Identité */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
                Identité
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Prénom" name="firstName"
                value={userData.firstName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Nom" name="lastName"
                value={userData.lastName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Date de naissance" type="date" name="dateOfBirth"
                value={userData.dateOfBirth} onChange={handleChange}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Téléphone" name="phone"
                value={userData.phone} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Compte */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
                Compte
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" name="email"
                value={userData.email} onChange={handleChange}
                disabled={isEdit}
                helperText={isEdit ? "Non modifiable" : ''} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Pseudo" name="pseudo"
                value={userData.pseudo} onChange={handleChange}
                helperText="Renseigné → badge App mobile dans le panel" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Rôle</InputLabel>
                <Select name="role" value={userData.role} onChange={handleChange} label="Rôle">
                  {ROLES.map(r => (
                    <MenuItem key={r.value} value={r.value}>
                      <Chip label={r.label} color={r.color} size="small"
                        sx={{ mr: 1, pointerEvents: 'none' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!isEdit && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Mot de passe" type="password" name="password"
                  value={userData.password} onChange={handleChange}
                  helperText="Vide = compte sans authentification (id manual_...)" />
              </Grid>
            )}

            {/* Cours — élèves et profs seulement */}
            {(isStudent || isTeacher) && (
              <>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
                    Cours {isTeacher ? 'enseignés' : 'inscrits'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={isStudent ? 6 : 12}>
                  <FormControl fullWidth>
                    <InputLabel>Cours</InputLabel>
                    <Select multiple name="selectedCourseIds"
                      value={userData.selectedCourseIds}
                      onChange={handleCourses}
                      input={<OutlinedInput label="Cours" />}
                      renderValue={sel => sel.length === 0
                        ? 'Aucun'
                        : sel.map(id => courses.find(c => c.id === id)?.title || id).join(', ')
                      }>
                      {loadingCourses
                        ? <MenuItem disabled>Chargement…</MenuItem>
                        : courses.map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            <Checkbox checked={userData.selectedCourseIds.includes(c.id)} />
                            <ListItemText primary={c.title} />
                          </MenuItem>
                        ))
                      }
                    </Select>
                    <FormHelperText>
                      {userData.selectedCourseIds.length} cours · multi-sélection possible
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {isStudent && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Formule</InputLabel>
                      <Select name="registrationPlan" value={userData.registrationPlan}
                        onChange={handleChange} label="Formule">
                        <MenuItem value="">Aucune</MenuItem>
                        {PLANS.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            {/* Info source */}
            <Grid item xs={12}>
              <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Badge <strong>Admin</strong> dans le panel.{' '}
                  {userData.pseudo
                    ? 'Pseudo renseigné → aussi détecté comme App si connecté via l\'app.'
                    : 'Sans pseudo → visible uniquement comme Admin.'}
                </Typography>
              </Alert>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/users')}>Annuler</Button>
                <Button type="submit" variant="contained" disabled={loading}
                  startIcon={loading
                    ? <CircularProgress size={18} sx={{ color: 'white' }} />
                    : <SaveIcon />}
                  sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}>
                  {loading ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}